import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const isCompact = scrolled;

  const leftMenuItems = [{ label: 'Corps', href: '#about' }];
  const rightMenuItems = [{ label: 'Kalender', href: '#veranstaltungen' }];

  const allMenuItems = [...leftMenuItems, ...rightMenuItems];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const scrollToSection = (href) => {
    if (location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      const offset =
        element.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }

    if (isMobile) setMobileOpen(false);
  };

  const navButtonStyle = {
    color: '#111',
    fontWeight: 400,
    fontSize: '0.95rem',
    textTransform: 'none',
    letterSpacing: '0.08em',
    position: 'relative',
    px: 1,
    transition: '0.3s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: 0,
      height: '2px',
      bgcolor: '#3a7b3e',
      transition: '0.3s ease',
    },
    '&:hover::after': {
      width: '100%',
    },
    '&:hover': {
      bgcolor: 'transparent',
      color: '#3a7b3e',
    },
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: isCompact
            ? 'rgba(255,255,255,0.92)'
            : 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(14px)',
          borderBottom: isCompact
            ? '1px solid rgba(0,0,0,0.06)'
            : '1px solid transparent',
          boxShadow: isCompact
            ? '0 2px 10px rgba(0,0,0,0.04)'
            : 'none',
          transition: '0.35s ease',
        }}
      >
        <Toolbar
          sx={{
            maxWidth: '1200px',
            width: '100%',
            mx: 'auto',
            px: { xs: 1, sm: 2, md: 3 },
            pt: isCompact ? 1 : { xs: 1.5, md: 3 },
            pb: isCompact ? 1 : { xs: 1, md: 2 },
            transition: '0.35s ease',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {/* MOBILE */}
          {isMobile ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box component={RouterLink} to="/">
                <Box
                  component="img"
                  src="/wappen.png"
                  alt="Logo"
                  sx={{
                    height: isCompact ? 64 : 78,
                    transition: '0.35s ease',
                  }}
                />
              </Box>

              <IconButton onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                gap: isCompact ? 6 : 2,
                transition: '0.35s ease',
              }}
            >
              {/* LINKS */}
              <Box sx={{ display: 'flex', gap: isCompact ? 2 : 3 }}>
                {leftMenuItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={navButtonStyle}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              {/* LOGO */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  mx: isCompact ? 2 : 6,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/wappen.png"
                  alt="Logo"
                  sx={{
                    height: isCompact ? 85 : 150,
                    transition: '0.35s ease',
                    transform: isCompact
                      ? 'translateY(0)'
                      : 'translateY(6px)',
                  }}
                />
              </Box>

              {/* RECHTS */}
              <Box
                sx={{
                  display: 'flex',
                  gap: isCompact ? 2 : 3,
                  alignItems: 'center',
                }}
              >
                {rightMenuItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={navButtonStyle}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* FLOATING INSTAGRAM BUTTON */}
      {/* FLOATING SOCIAL BUTTONS */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 84, sm: 24 },
          right: { xs: 16, sm: 84, md: 116 },
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        {/* Instagram Button */}
        <IconButton
          href="https://www.instagram.com/corps.guestphalia.berlin?igsh=MXZsaTRoZ2gxaHRocQ=="
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            width: { xs: 46, sm: 54 },
            height: { xs: 46, sm: 54 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f58529, #dd2a7b, #8134af)',
            color: '#fff',
            boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
            transition: '0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 14px 30px rgba(0,0,0,0.3)',
            },
          }}
        >
          <InstagramIcon />
        </IconButton>
      </Box>

      {/* MOBILE DRAWER */}
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        <Box sx={{ width: { xs: 250, sm: 280 }, p: 2 }}>
          <List>
            {allMenuItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => scrollToSection(item.href)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'rgba(58,123,62,0.08)',
                    },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
